import { useEffect, useMemo, useRef, useState } from 'react';
import { throttle } from 'lodash-es';

import scrollPage from '../../../../utils/scrollPage';
import { getScrollableContainer } from '../../../../utils/helpers';

const PAGE_HEADER_HEIGHT = 72;
const STICKY_HEADER_HEIGHT = 118;

// Keep `sections` memoized because this hook relies on them for scroll tracking and effect deps.
export default function useDashboardNav(sections, stickySentinelRef) {
  const scrollableAreaRef = useRef(getScrollableContainer());
  const [activeSection, setActiveSection] = useState(sections[0].id);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    let observer;

    if (stickySentinelRef?.current) {
      const isWindow = scrollableAreaRef.current === window;

      observer = new IntersectionObserver(
        ([entry]) => {
          setIsSticky(!entry.isIntersecting);
        },
        {
          root: isWindow ? null : scrollableAreaRef.current,
          threshold: 0,
        }
      );
      observer.observe(stickySentinelRef.current);
    }

    return () => {
      observer?.disconnect();
    };
  }, [stickySentinelRef]);

  useEffect(() => {
    const isWindow = scrollableAreaRef.current === window;
    const sectionEntries = sections
      .map(({ id }) => ({ id, element: document.getElementById(id) }))
      .filter(({ element }) => element);

    const handleScroll = throttle(() => {
      const target = isWindow ? window : scrollableAreaRef.current;
      const scrollTop = isWindow ? window.pageYOffset : target.scrollTop;
      const scrollHeight = isWindow ? document.documentElement.scrollHeight : target.scrollHeight;
      const clientHeight = isWindow ? window.innerHeight : target.clientHeight;

      if (scrollHeight - scrollTop - clientHeight <= 50) {
        setActiveSection(sections[sections.length - 1].id);
        return;
      }

      const offset = STICKY_HEADER_HEIGHT + PAGE_HEADER_HEIGHT;

      for (let i = sectionEntries.length - 1; i >= 0; i--) {
        if (Math.round(sectionEntries[i].element.getBoundingClientRect().top) <= offset) {
          setActiveSection(sectionEntries[i].id);
          return;
        }
      }
      setActiveSection(sections[0].id);
    }, 100);

    const target = isWindow ? window : scrollableAreaRef.current;
    target.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      target.removeEventListener('scroll', handleScroll);
      handleScroll.cancel();
    };
  }, [sections]);

  const active = useMemo(
    () => sections.find((s) => s.id === activeSection) || sections[0],
    [activeSection, sections]
  );

  return { isSticky, active };
}

export const scrollToSection = (sectionId) => {
  const el = document.getElementById(sectionId);
  if (!el) return;

  const scrollableArea = getScrollableContainer();
  const isWindow = scrollableArea === window;
  const containerOffset = isWindow ? 0 : scrollableArea.getBoundingClientRect().top;
  const destination =
    el.getBoundingClientRect().top +
    (isWindow ? window.pageYOffset : scrollableArea.scrollTop) -
    containerOffset -
    STICKY_HEADER_HEIGHT;

  scrollPage({ destination, scrollableArea });
};
